import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AppContext = createContext(null);

export function useApp() {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useApp must be used within an AppContextProvider');
    }
    return context;
}

export function AppContextProvider({ children }) {
    const [user, setUserState] = useState(null);
    const [accessTkn, setAccessTknState] = useState(null);
    const [refreshTkn, setRefreshTknState] = useState(null);
    const [isAuth, setIsAuthState] = useState(false);

    const [categories, setCategoriesState] = useState([]);
    const [amenities, setAmenitiesState] = useState([]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        try {
            const savedUser = localStorage.getItem('user');
            const savedAccess = localStorage.getItem('accessTkn');
            const savedRefresh = localStorage.getItem('refreshTkn');

            const userObj = savedUser ? JSON.parse(savedUser) : null;
            const hasValidToken = !!savedAccess;

            setUserState(userObj);
            setAccessTknState(savedAccess);
            setRefreshTknState(savedRefresh);
            setIsAuthState(hasValidToken);
        } catch (e) {
            console.warn('Failed to restore auth from localStorage', e);
        }
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        try {
            const savedCats = sessionStorage.getItem('categories');
            const savedAmen = sessionStorage.getItem('amenities');

            if (savedCats) setCategoriesState(JSON.parse(savedCats));
            if (savedAmen) setAmenitiesState(JSON.parse(savedAmen));
        } catch (e) {
            console.warn('Failed to restore session data', e);
        }
    }, []);

    const setUser = useCallback((data) => {
        setUserState(data);
        if (typeof window !== 'undefined') {
            if (data) {
                localStorage.setItem('user', JSON.stringify(data));
            } else {
                localStorage.removeItem('user');
            }
        }
    }, []);

    const setCredentials = useCallback(({ access, refresh }) => {
        const now = new Date().toISOString();
        setUserState(prev => prev || {});
        setAccessTknState(access);
        setRefreshTknState(refresh);
        setIsAuthState(true);

        if (typeof window !== 'undefined') {
            localStorage.setItem('accessTkn', access);
            localStorage.setItem('refreshTkn', refresh);
            localStorage.setItem('refreshTime', now);
        }
    }, []);

    const logout = useCallback(() => {
        setUserState(null);
        setAccessTknState(null);
        setRefreshTknState(null);
        setIsAuthState(false);

        if (typeof window !== 'undefined') {
            localStorage.removeItem('user');
            localStorage.removeItem('accessTkn');
            localStorage.removeItem('refreshTkn');
            localStorage.removeItem('refreshTime');
            sessionStorage.clear(); 
        }
    }, []);

    const setCategories = useCallback((data) => {
        setCategoriesState(data);
        if (typeof window !== 'undefined') {
            if (data?.length) {
                sessionStorage.setItem('categories', JSON.stringify(data));
            } else {
                sessionStorage.removeItem('categories');
            }
        }
    }, []);

    const setAmenities = useCallback((data) => {
        setAmenitiesState(data);
        if (typeof window !== 'undefined') {
            if (data?.length) {
                sessionStorage.setItem('amenities', JSON.stringify(data));
            } else {
                sessionStorage.removeItem('amenities');
            }
        }
    }, []);

    const clearSessionData = useCallback(() => {
        setCategoriesState([]);
        setAmenitiesState([]);
        if (typeof window !== 'undefined') {
            sessionStorage.removeItem('categories');
            sessionStorage.removeItem('amenities');
        }
    }, []);

    useEffect(() => {
        if (!accessTkn) return;
        try {
            const payload = JSON.parse(atob(accessTkn.split('.')[1]));
            const expMs = payload.exp * 1000;
            const timeout = expMs - Date.now();
            if (timeout > 0) {
                const timer = setTimeout(() => logout(), timeout);
                return () => clearTimeout(timer);
            } else {
                logout();
            }
        } catch (e) {
            console.warn('Token parse error', e);
            logout();
        }
    }, [accessTkn, logout]);

    return (
        <AppContext.Provider
            value={{
                user,
                isAuth,
                accessTkn,
                refreshTkn,
                setUser,
                setCredentials,
                logout,
                categories,
                amenities,
                setCategories,
                setAmenities,
                clearSessionData,
            }}
        >
            {children}
        </AppContext.Provider>
    );
}

export default AppContext;